import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { getTranslations, getLanguages } from "../utils/config-calls";
import debug from "@commons-shared/debug";
import { config } from "../config/environment";

const DEFAULT_LANGUAGE = 'FR';

// Variables globales pour permettre l'accès aux traductions en dehors du contexte React
let globalTranslations = {};
let globalLanguage = DEFAULT_LANGUAGE;

/**
 * Fonction utilitaire pour accéder aux traductions en dehors des composants React
 * @param {string} pageName - Nom de la page
 * @param {string} key - Clé de traduction
 * @param {string} defaultText - Texte par défaut
 * @param {Object} params - Paramètres pour la substitution
 * @returns {string} - Texte traduit
 */
export function getTranslation(pageName, key, defaultText = key, params = {}) {
  const cacheKey = `${globalLanguage}:${pageName}`;
  const pageTranslations = globalTranslations[cacheKey] || {};
  let translation = pageTranslations[key] || defaultText;
  
  // Substitution des paramètres
  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const placeholder = `{{${paramKey}}}`;
      translation = translation.replace(new RegExp(placeholder, 'g'), paramValue);
    });
  }
  
  return translation;
}

// Create context
export const TranslationContext = createContext({
  translations: {},
  language: DEFAULT_LANGUAGE,
  languages: [],
  changeLanguage: () => {},
  loadPageTranslations: () => {},
  t: () => {},
  isLoading: false,
  error: null,
});

export const useTranslation = () => useContext(TranslationContext);

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useLocalStorage('ui_language', DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track current translations for cache checking without triggering useCallback dependencies
  const translationsRef = useRef({});
  // Use ref to track loading states to prevent concurrent requests
  const loadingRef = useRef(new Set());
  
  // Update ref when translations change
  useEffect(() => {
    translationsRef.current = translations;
  }, [translations]);

  // Fetch available languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await getLanguages();
        const languagesData = response.success ? response.languages : [];
        setLanguages(languagesData);
      } catch (err) {
        debug.error(config, 'Failed to load available languages:', err);
        setError('Failed to load available languages');
      }
    };

    fetchLanguages();
  }, []);

  // Load translations for a specific page
  const loadPageTranslations = useCallback(async (pageName) => {
    // Check if translations are already loaded for this page and language
    const cacheKey = `${language}:${pageName}`;
    
    // Use ref to check current translations without causing dependency issues
    if (translationsRef.current[cacheKey]) {
      debug.log(config, `Using cached translations for ${cacheKey}`);
      return;
    }
    
    // Check if already loading this page to prevent concurrent requests
    if (loadingRef.current.has(cacheKey)) {
      debug.log(config, `Already loading translations for ${cacheKey}, skipping duplicate request`);
      return;
    }
    
    debug.log(config, `Loading translations for ${language}/${pageName}`);
    loadingRef.current.add(cacheKey);
    setIsLoading(true);
    setError(null);

    try {
      const response = await getTranslations(language, pageName);
      
      if (!response.success) {
        throw new Error(`Failed to fetch translations: ${response.error || 'Unknown error'}`);
      }
      
      const translationsData = response.translations || {};
      
      // Cache translations with language:page key
      setTranslations(prev => ({
        ...prev,
        [cacheKey]: translationsData
      }));
      
      debug.log(config, `Translations loaded for ${cacheKey}`, {
        count: Object.keys(translationsData).length
      });
    } catch (err) {
      debug.error(config, `Failed to load translations for ${language}/${pageName}:`, err);
      setError(`Failed to load translations for ${pageName}`);
    } finally {
      loadingRef.current.delete(cacheKey);
      setIsLoading(false);
    }
  }, [language]);

  // Change language and clear translations cache
  const changeLanguage = useCallback(async (newLanguage) => {
    if (newLanguage === language) {
      debug.log(config, `Language already set to ${newLanguage}`);
      return;
    }
    
    debug.log(config, `Changing language from ${language} to ${newLanguage}`);
    
    // Clear translations cache when changing language
    setTranslations({});
    setError(null);
    setLanguage(newLanguage);
    
    debug.log(config, `Language changed to ${newLanguage}, cache cleared`);
  }, [language, setLanguage]);

  // Get translation by key with parameter substitution
  const t = useCallback((pageName, key, defaultText = key, params = {}) => {
    const cacheKey = `${language}:${pageName}`;
    const pageTranslations = translations[cacheKey] || {};
    let translation = pageTranslations[key] || defaultText;

    // Log missing translations only if page translations exist
    if (!pageTranslations[key] && Object.keys(pageTranslations).length > 0) {
      debug.warn(config, `Missing translation: ${language}.${pageName}.${key}`, { defaultText });
    }
    
    // Parameter substitution
    if (params && typeof params === 'object') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        const placeholder = `{{${paramKey}}}`;
        translation = translation.replace(new RegExp(placeholder, 'g'), paramValue);
      });
    }
    
    return translation;
  }, [translations, language]);

  // Mettre à jour les variables globales quand les traductions ou la langue changent
  useEffect(() => {
    globalTranslations = translations;
    globalLanguage = language;
  }, [translations, language]);

  return (
    <TranslationContext.Provider value={{ 
      t, 
      language, 
      changeLanguage, 
      loadPageTranslations, 
      translations,
      languages,
      isLoading,
      error
    }}>
      {children}
    </TranslationContext.Provider>
  );
};
